import styled, { css } from 'styled-components'
import { Button } from 'examkrackers-components'
import { QuizProgressStatus } from './quiz.types'

/** Viewport width at or below this uses full-screen quiz layout (matches Modal behavior). */
export const quizModalMobileMaxWidthPx = 768

/** Desktop quiz modal width. Mobile stays full-screen via the media query below. */
export const quizModalDesktopWidthPx = 646

const progressColorByStatus: Record<QuizProgressStatus, string> = {
  unanswered: '#C5C6C8',
  correct: '#AAFB3F',
  incorrect: '#FF383C'
}

export const QuizModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 650px;
  width: ${quizModalDesktopWidthPx}px;
  max-width: ${quizModalDesktopWidthPx}px;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #d7d7d7;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  font-family: Verdana !important;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 20px;
    background: #2f9900;
    border-radius: 0 0 18px 18px;
    z-index: 3;
    pointer-events: none;
  }

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
    max-width: 100%;
    height: 100%;
    min-height: 0;
    max-height: none;
    flex: 1 1 auto;
    border-radius: 0;

    &::after {
      border-radius: 0;
    }
  }
`

export const QuizHeader = styled.div`
  background: #339900;
  color: #fff;
  padding: 8px 12px 0 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  height: 110px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`

export const HeadingTooltipTrigger = styled.div`
  font-size: 14px;
  line-height: 1;
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
`

/** Flex row so title (and Tooltip root) shrink for ellipsis on narrow viewports / iOS. */
export const HeadingTitleClip = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  > * {
    flex: 1 1 0%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }
`

export const QuizHeading = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  font-size: 20px;
  line-height: 1.1;
  font-weight: 700;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const QuizBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 20px 26px;
  display: flex;
  flex-direction: column;
`

export const HeaderTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-right: 22px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`

export const HeadingRow = styled.div`
  background: #339900;
  padding-bottom: 8px;
  text-align: start;
  padding-left: 12px;
  padding-right: 12px;
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
`

export const HeaderDivider = styled.div`
  height: 23px;
  background: #82bc64;
  margin-top: 1px;
`

export const HeaderCloseButton = styled.button`
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  width: 19px;
  height: 19px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  position: absolute;
  svg {
    font-size: 12px;
    font-size: 14px;
    width: 14px;
    height: 14px;
  }

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 28px;
    height: 28px;
    position: absolute;
    svg {
      font-size: 19px;
      width: 19px;
      height: 19px;
    }
  }
`

export const AnswerToggleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: 12px;
  white-space: nowrap;
  line-height: 1;
`

export const ToggleLabel = styled.span`
  font-size: 11px;
`

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

export const ToggleButton = styled.button<{ enabled: boolean }>`
  border: 0;
  width: 30px;
  height: 16px;
  border-radius: 30px;
  background: ${({ enabled }) => (enabled ? '#8bc66f' : '#d2d2d2')};
  position: relative;
  cursor: pointer;
  padding: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ enabled }) => (enabled ? '16px' : '2px')};
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    transition: left 150ms ease;
  }
`

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 420px;
  gap: 20px;
  text-align: center;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    min-height: 0;
    flex: 1;
  }
`

export const LoadingTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #111;
`

export const LoadingDot = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f6b75f;
  animation: pulse 1000ms ease-in-out infinite;

  @keyframes pulse {
    0% {
      transform: scale(0.8);
      opacity: 0.6;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(0.8);
      opacity: 0.6;
    }
  }
`

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 18px;
  min-height: 420px;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    min-height: 0;
    flex: 1;
  }
`

export const ErrorMark = styled.div`
  color: #dc2626;
  font-size: 72px;
  line-height: 1;
`

export const ErrorTitle = styled.div`
  font-size: 16px;
  color: #444444;
`

export const ErrorText = styled.div`
  font-size: 14px;
  color: #444444;
`

export const ResultsSummary = styled.div`
  text-align: center;
  margin-bottom: 20px;
  overflow: visible;
`

export const ScoreLine = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #444444;
`

export const PercentageLine = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #444444;
`

export const ResultsHeading = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin: 0px 0 20px;
  text-align: left;
`

export const FeedbackText = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-top: 5px;
  color: #e83e2e;
`

export const ScheduleText = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-top: 5px;
  color: #444444;
`

export const ScheduleHighlight = styled.span`
  color: #247ef4;
`

export const CongratulationsPhrase = styled.span`
  position: relative;
  display: inline-block;
  overflow: visible;
`

export const CongratulationsText = styled.span`
  position: relative;
  z-index: 2;
  color: #e83e2e;
  font-weight: 700;
`

export const ResultBlock = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  // border-bottom: 1px solid #d7d7d7;
  text-align: left;
  font-size: 14px;
  font-weight: 400;
`

export const ResultQuestion = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 400;
  color: #444444;
`

export const ResultLabel = styled.div`
  font-size: 14px;
  margin-bottom: 2px;
  font-weight: 400;
  color: #444444;
`

export const ResultLabelLabel = styled.div`
  font-size: 14px;
  margin-bottom: 15px;
  font-weight: 400;
  color: #444444;
`

export const ResultState = styled.span<{ isCorrect: boolean }>`
  color: ${({ isCorrect }) => (isCorrect ? '#1b8a33' : '#b91c1c')};
  font-weight: 700;
  font-size: 14px;
  color: #444444;
`

export const ConceptsContainer = styled.div`
  margin: 20px 0 50px;
  font-size: 16px;
  color: #444444;
  line-height: 1.45;
  text-align: left;
`

export const WeakConceptsList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
  list-style-type: disc;

  li {
    margin-bottom: 6px;
    font-size: 14px;
    color: #444444;
  }
`

export const StickyFooter = styled.div`
  position: sticky;
  bottom: -13px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 14px;
`

export const FooterButtons = styled.div<{ finalPercentageCheck: boolean }>`
  display: flex;
  width: 100%;
  padding: 10px 0 0;
  justify-content: ${({ finalPercentageCheck }) =>
    !finalPercentageCheck ? 'space-evenly' : 'center'};
  gap: 8px;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 12px;
    padding: 10px 12px 0;
  }
`

export const QuizMeAgainButton = styled(Button)`
  width: 208px;
  font-size: 12px;
  font-weight: 700;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
    max-width: 100%;
  }
`
export const TakeQuizButton = styled(Button)`
  position: relative;
  overflow: visible;
  width: 190px;
  font-size: 12px;
  font-weight: 700;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
    max-width: 100%;
  }
`
export const NextQuizButton = styled(Button)`
  position: relative;
  overflow: visible;
  width: 165px;
  font-size: 12px;
  font-weight: 700;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
    max-width: 100%;
  }
`

export const NextQuizButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: 165px;
  overflow: visible;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
    max-width: 100%;
  }
`

export const NextQuizDueBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: #ea4335;
  color: #ffffff;
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  box-sizing: border-box;
`

export const ResultBtnLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 10px;
  font-family: Verdana;
  color: #444444;
  margin-bottom: 4px;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    text-align: center;
  }
`
export const OutputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
  }
`

export const SeeQuizDiagnosticsLink = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 144px;
  height: 30px;
  margin: 6px 0 0;
  padding: 7px 18px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0;
  color: #444444;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-thickness: 1px;
  text-underline-offset: 1px;
  text-underline-position: under;
  vertical-align: middle;
  cursor: pointer;
  text-align: center;
  opacity: 1;
  white-space: nowrap;

  &:hover {
    color: #c0752c;
  }

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`

export const IncludeInAlgorithmRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 12px 0 0;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: #444444;
`

export const IncludeInAlgorithmLabel = styled.span`
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`
export const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`

export const QuestionNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`

export const QuestionNavButton = styled.button<{ $isActive: boolean }>`
  border: 0;
  background: transparent;
  padding: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isActive }) => ($isActive ? 'pointer' : 'default')};
  flex-shrink: 0;

  img {
    width: 16px;
    height: 16px;
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0.4)};
    display: block;
  }

  ${({ $isActive }) =>
    $isActive &&
    `
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  `}

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 1px;
  }
`

export const ProgressLabel = styled.div<{ variant?: 'header' | 'body' }>`
  font-size: 14px;
  color: ${({ variant }) => (variant === 'header' ? '#fff' : '#222')};
  min-width: ${({ variant }) => (variant === 'header' ? 'auto' : '100px')};
  white-space: nowrap;
`

export const ProgressScore = styled.div<{ variant?: 'header' | 'body' }>`
  font-size: 14px;
  color: ${({ variant }) => (variant === 'header' ? '#fff' : '#222')};
  min-width: ${({ variant }) => (variant === 'header' ? '96px' : '70px')};
  text-align: justify;
`

export const ProgressBars = styled.div`
  display: flex;
`

export const ProgressBar = styled.div<{ status: QuizProgressStatus }>`
  height: 7px;
  width: 9px;
  border-radius: 2px;
  background: ${({ status }) => progressColorByStatus[status]};
  margin-right: 1px;
`

export const QuestionPrompt = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  margin-bottom: 16px;
  padding: 0 4px;
  color: #3a3a3a;

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 8px 0;
    border-radius: 4px;
  }
`

export const QuizQuestionScreen = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`

export const QuizQuestionContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 8px 12px;
`

export const QuizNextFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  padding: 10px 0 14px;
  background: #fff;
  z-index: 2;
`

export const QuizNextButton = styled(Button)`
  && {
    min-width: 71px;
    width: max-content;
    height: 41px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
  }
`

export const AnswersCard = styled.div`
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 1px 6px 0 #00000026;
  padding: 8px;
  margin: 0;
`

export const AnswersCardHeader = styled.button<{
  isVisible: boolean
  isOpen: boolean
}>`
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  color: #4a4a4a;
  font-weight: 400;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: ${({ isVisible, isOpen }) =>
    !isVisible || isOpen ? '10px' : '0'};
  transition: margin-bottom ${({ isOpen }) => (isOpen ? '220ms' : '180ms')} ease;
  &:disabled {
    cursor: default;
    opacity: 1;
  }
`

export const AnswersHeaderLabel = styled.span`
  color: #4a4a4a;
  font-weight: 400;
  font-size: 12px;
`

export const AnswersHeaderCaret = styled.span`
  color: #f3a32b;
  font-weight: 700;
  height: 14px;
`

export const OptionList = styled.div<{
  isVisible: boolean
  expandedHeight: number
  $isIosLayoutFix?: boolean
}>`
  display: flex;
  width: 100%;
  overflow: hidden;
  transition: max-height ${({ isVisible }) => (isVisible ? '260ms' : '180ms')}
    ${({ isVisible }) =>
      isVisible
        ? 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        : 'cubic-bezier(0.4, 0, 1, 1)'};
  transition-delay: 0ms;

  > div {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }

  ${({ $isIosLayoutFix, isVisible, expandedHeight }) =>
    $isIosLayoutFix &&
    css`
      max-height: ${!isVisible
        ? '0px'
        : expandedHeight <= 0
        ? 'min(2400px, 85vh)'
        : `${Math.ceil(expandedHeight) + 64}px`};
      overflow: ${isVisible ? 'visible' : 'hidden'};

      > div {
        gap: 10px;
        min-width: 0;
      }
    `}
`

const optionStateTextColor: Record<
  'default' | 'correct' | 'incorrect' | 'revealed',
  string
> = {
  default: '#444444',
  correct: '#339900',
  incorrect: '#A51417',
  revealed: '#444444'
}

export const OptionButton = styled.div<{
  state: 'default' | 'correct' | 'incorrect' | 'revealed'
}>`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: transparent;
  cursor: ${({ state }) => (state === 'default' ? 'pointer' : 'default')};
`

export const OptionChoiceRow = styled.div<{
  state: 'default' | 'correct' | 'incorrect' | 'revealed'
  $isIosLayoutFix?: boolean
}>`
  width: 100%;
  box-sizing: border-box;
  border-radius: 6px;
  padding: 6px 4px;
  background: ${({ state }) => {
    if (state === 'correct') {
      return '#E8F9E1'
    }

    if (state === 'incorrect') {
      return '#FFDEDC'
    }

    if (state === 'revealed') {
      return '#E8E8E8'
    }

    return 'transparent'
  }};
  color: ${({ state }) => optionStateTextColor[state]};
  font-weight: 400;
  font-size: 14px;
  line-height: 1.35;
  transform: translateX(0);
  transition: background-color 240ms ease,
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
  min-height: 40px;
  display: flex;
  align-items: center;

  ${({ state }) =>
    state === 'default' &&
    `
      &:hover {
        transform: translateX(2px);
      }

      &:active {
        transform: translateX(2px) scale(0.995);
      }
    `}

  & > div > div > div > span {
    color: ${({ state }) => optionStateTextColor[state]};
  }

  ${({ $isIosLayoutFix }) =>
    $isIosLayoutFix &&
    css`
      flex-shrink: 0;
      min-height: min-content;
      padding: 8px 6px;
      will-change: auto;
      transform: none;
      transition: background-color 240ms ease;
    `}
`

export const OptionLetter = styled.span<{ $isIosLayoutFix?: boolean }>`
  font-weight: 700;
  font-size: 14px;
  min-width: 22px;
  flex-shrink: 0;
  align-self: flex-start;
  line-height: 1.35;
  margin-right: 8px;
  color: inherit;
  ${({ $isIosLayoutFix }) =>
    $isIosLayoutFix &&
    css`
      line-height: 1.45;
    `}
`

export const OptionLabel = styled.span<{
  $isIosLayoutFix?: boolean
  $hasRichContent?: boolean
}>`
  display: ${({ $hasRichContent }) => ($hasRichContent ? 'block' : 'inline-flex')};
  align-items: flex-start;
  gap: 1px;
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.35;
  color: inherit;
  text-align: left;
  word-break: break-word;

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 8px 0;
    border-radius: 4px;
  }

  ${({ $isIosLayoutFix }) =>
    $isIosLayoutFix &&
    css`
      display: flex;
      flex-wrap: wrap;
      flex: 1 1 auto;
      min-width: 0;
      line-height: 1.45;
      overflow-wrap: anywhere;
    `}

  ${({ $hasRichContent }) =>
    $hasRichContent &&
    css`
      display: block;
      width: 100%;
    `}
`

export const OptionRadioWrapper = styled.div<{ $isIosLayoutFix?: boolean }>`
  & > div {
    align-items: flex-start;
    width: 100%;
    & > div:first-child {
      margin-top: 2px;
    }
  }

  .radio-icon {
    align-self: flex-start;
    flex-shrink: 0;
  }

  .radio-label {
    transform: none;
    align-self: flex-start;
    flex: 1;
    min-width: 0;
    text-align: justify;
    display: flex;
    margin-left: 16px;
  }

  ${({ $isIosLayoutFix }) =>
    $isIosLayoutFix &&
    css`
      width: 100%;
      min-width: 0;
    `}
`

export const CloseIconWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`

export const SuccessAction = styled.div<{ $isIosLayoutFix?: boolean }>`
  display: flex;
  justify-content: start;
  align-items: flex-start;
  gap: 6px;
  width: 100%;

  img,
  svg {
    flex-shrink: 0;
    align-self: flex-start;
  }

  ${({ $isIosLayoutFix }) =>
    $isIosLayoutFix &&
    css`
      gap: 8px;
      width: 100%;
      min-width: 0;
    `}
`

export const ImageSize = styled.span`
  font-size: 10px;
`

export const OptionExplanation = styled.div`
  margin: 4px 0 2px;
  padding: 4px 8px 6px 4px;
  background: #ffffff;
  border-radius: 0;
  font-family: Verdana, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 1.45;
  color: #6c5f68;
  text-align: left;

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 8px 0;
    border-radius: 4px;
  }
`

export const RevealedOptionIcon = styled.svg`
  flex-shrink: 0;
  align-self: flex-start;
  width: 15px;
  height: 15px;
  margin-top: 3px;
`

export const TryAgainButton = styled.span`
  color: #444444;
  font-style: italic;
  font-size: 12px;
  width: 100%;
  display: flex;
  margin-bottom: 16px;
`

export const ShowAnswerChoicesButton = styled.button`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 0 4px;
  border: 0;
  background: transparent;
  font-family: Verdana, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  color: #444444;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`

export const AnswerChoicesInfoModalInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  text-align: center;
`

export const AnswerChoicesInfoCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1a1a1a;
  cursor: pointer;
  z-index: 2;

  svg {
    width: 14px;
    height: 14px;
  }

  &:focus-visible {
    outline: 2px solid #f8961a;
    outline-offset: 2px;
  }
`

export const AnswerChoicesInfoIcon = styled.img`
  display: block;
  width: 42px;
  height: 42px;
  margin: 0 0 34px;
`

export const AnswerChoicesInfoTitle = styled.h2`
  margin: 0 0 20px;
  font-family: Arial, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.3;
  text-align: center;
  color: #1a1a1a;
`

export const AnswerChoicesInfoBody = styled.p`
  margin: 0 0 34px;
  font-family: Arial, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.45;
  text-align: center;
  color: #444444;
`

export const AnswerChoicesInfoActions = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 16px;

  button {
    width: 174px;
  }
`

export const AnswerChoicesInfoDontShowAgain = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.2;
  color: #444444;
`

export const AnswerChoicesInfoDontShowAgainText = styled.span`
  cursor: pointer;
`
